import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import NotificationsRoundedIcon from "@mui/icons-material/NotificationsRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import CampaignRoundedIcon from "@mui/icons-material/CampaignRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";

import { BRAND_BLUE, BRAND_GREEN } from "../../theme/colors";
import { formatDateTime } from "../../utils/formatters";
import BrandSpinner from "../../shared/components/BrandSpinner";
import {
  fetchNotifications,
  fetchUnreadCount,
  markAllNotificationsRead,
  markNotificationRead,
} from "./notificationService";

import NotificationVideo from "./NotificationVideo";

const POLL_MS = 60000;
const NOTIFICATION_TIMESTAMP_OPTIONS = {
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
};

const severityConfig = {
  info: {
    icon: InfoOutlinedIcon,
    color: BRAND_BLUE,
    bg: alpha(BRAND_BLUE, 0.1),
  },
  success: {
    icon: CheckCircleOutlineRoundedIcon,
    color: BRAND_GREEN,
    bg: alpha(BRAND_GREEN, 0.12),
  },
  warning: {
    icon: WarningAmberRoundedIcon,
    color: "#b26a00",
    bg: "rgba(255, 183, 77, 0.16)",
  },
  error: {
    icon: ErrorOutlineRoundedIcon,
    color: "#c62828",
    bg: "rgba(239, 83, 80, 0.14)",
  },
};

function isExternalLink(url) {
  return /^https?:\/\//i.test(url || "");
}

function isProspectAddedNotification(notification) {
  const text = `${notification?.title || ""} ${notification?.message || ""}`.toLowerCase();
  return (
    text.includes("new weekly prospect list") ||
    text.includes("new prospect company added") ||
    text.includes("new prospect added")
  );
}

function notificationHasAction(notification) {
  return Boolean(notification?.link_url) && !isProspectAddedNotification(notification);
}

export default function NotificationBell({ onNavigate }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingCount, setLoadingCount] = useState(false);
  const [error, setError] = useState("");
  const [markingIds, setMarkingIds] = useState([]);
  const [markingAll, setMarkingAll] = useState(false);
  const [highlightedId, setHighlightedId] = useState(null);

  const open = Boolean(anchorEl);

  const loadUnreadCount = useCallback(async () => {
    try {
      setLoadingCount(true);
      const count = await fetchUnreadCount();
      setUnreadCount(count);
    } catch {
      // Keep the bell resilient even if polling fails.
    } finally {
      setLoadingCount(false);
    }
  }, []);

  const loadNotifications = useCallback(async () => {
    try {
      setLoadingList(true);
      setError("");
      const items = await fetchNotifications({ limit: 12 });
      setNotifications(items);
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          err?.message ||
          "Unable to load notifications.",
      );
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => {
    loadUnreadCount();
    const intervalId = window.setInterval(loadUnreadCount, POLL_MS);
    return () => window.clearInterval(intervalId);
  }, [loadUnreadCount]);

  useEffect(() => {
    if (open) {
      loadNotifications();
    }
  }, [open, loadNotifications]);

  useEffect(() => {
    const handleOpenCenter = (event) => {
      const trigger = document.querySelector('[aria-label="open notifications"]');
      if (trigger) {
        setAnchorEl(trigger);
      }
      setHighlightedId(event?.detail?.notificationId || null);
    };

    window.addEventListener("be:open-notification-center", handleOpenCenter);
    return () => {
      window.removeEventListener("be:open-notification-center", handleOpenCenter);
    };
  }, []);

  useEffect(() => {
    if (!highlightedId || !notifications.length) {
      return undefined;
    }

    const element = document.getElementById(`notification-item-${highlightedId}`);
    if (!element) {
      return undefined;
    }

    element.scrollIntoView({ block: "center", behavior: "smooth" });
    const timeoutId = window.setTimeout(() => setHighlightedId(null), 2200);
    return () => window.clearTimeout(timeoutId);
  }, [highlightedId, notifications]);

  const unreadItems = useMemo(
    () => notifications.filter((item) => !item.is_read).length,
    [notifications],
  );

  const setItemReadOptimistically = useCallback((notificationId, readAt) => {
    setNotifications((prev) =>
      prev.map((item) =>
        item.id === notificationId
          ? { ...item, is_read: true, read_at: readAt || new Date().toISOString() }
          : item,
      ),
    );
    setUnreadCount((prev) => Math.max(0, prev - 1));
  }, []);

  const handleNotificationAction = useCallback(
    async (notification, openLink = true) => {
      const wasUnread = !notification.is_read;
      const fallbackItems = notifications;
      const fallbackUnreadCount = unreadCount;

      if (wasUnread) {
        setMarkingIds((prev) => [...prev, notification.id]);
        setItemReadOptimistically(notification.id);
      }

      try {
        if (wasUnread) {
          const result = await markNotificationRead(notification.id);
          setNotifications((prev) =>
            prev.map((item) =>
              item.id === notification.id
                ? { ...item, is_read: true, read_at: result?.read_at || item.read_at }
                : item,
            ),
          );
        }
      } catch (err) {
        if (wasUnread) {
          setNotifications(fallbackItems);
          setUnreadCount(fallbackUnreadCount);
        }
        setError(
          err?.response?.data?.detail ||
            err?.message ||
            "Unable to update notification.",
        );
        return;
      } finally {
        if (wasUnread) {
          setMarkingIds((prev) => prev.filter((id) => id !== notification.id));
        }
      }

      if (openLink && notificationHasAction(notification)) {
        if (isExternalLink(notification.link_url)) {
          window.open(notification.link_url, "_blank", "noopener,noreferrer");
        } else if (typeof onNavigate === "function") {
          onNavigate(notification.link_url);
        } else {
          window.location.assign(notification.link_url);
        }
      }
    },
    [notifications, onNavigate, setItemReadOptimistically, unreadCount],
  );

  const handleMarkAllRead = useCallback(async () => {
    const unreadIds = notifications.filter((item) => !item.is_read).map((item) => item.id);
    if (!unreadIds.length) return;

    const fallbackItems = notifications;
    const fallbackCount = unreadCount;
    const now = new Date().toISOString();

    setMarkingAll(true);
    setNotifications((prev) =>
      prev.map((item) => ({
        ...item,
        is_read: true,
        read_at: item.read_at || now,
      })),
    );
    setUnreadCount(0);

    try {
      await markAllNotificationsRead();
    } catch (err) {
      setNotifications(fallbackItems);
      setUnreadCount(fallbackCount);
      setError(
        err?.response?.data?.detail ||
          err?.message ||
          "Unable to mark notifications as read.",
      );
    } finally {
      setMarkingAll(false);
    }
  }, [notifications, unreadCount]);

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton
          aria-label="open notifications"
          onClick={(event) => setAnchorEl(event.currentTarget)}
          sx={{
            width: 42,
            height: 42,
            color: "#fff",
            border: "2px solid rgba(255,255,255,0.92)",
            backgroundColor: alpha("#ffffff", 0.08),
            boxShadow: unreadCount
              ? `0 0 0 4px ${alpha(BRAND_GREEN, 0.16)}`
              : "none",
            transition: "all .2s ease",
            "&:hover": {
              backgroundColor: alpha("#ffffff", 0.18),
              transform: "translateY(-1px)",
            },
          }}
        >
          <Badge
            color="error"
            overlap="circular"
            badgeContent={unreadCount > 99 ? "99+" : unreadCount}
            invisible={unreadCount < 1}
          >
            <NotificationsRoundedIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            mt: 1.5,
            width: { xs: "calc(100vw - 24px)", sm: 410 },
            maxWidth: 410,
            maxHeight: 560,
            display: "flex",
            flexDirection: "column",
            borderRadius: 3,
            border: `1px solid ${alpha(BRAND_BLUE, 0.14)}`,
            boxShadow: "0 22px 48px rgba(0, 36, 77, 0.18)",
            overflow: "hidden",
          },
        }}
      >
        <Box
          sx={{
            px: 2.25,
            py: 1.75,
            background: `linear-gradient(135deg, ${alpha(BRAND_BLUE, 0.98)} 0%, #0a427f 58%, ${BRAND_GREEN} 100%)`,
            color: "#fff",
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
            <Box>
              <Typography sx={{ fontWeight: 800, letterSpacing: 0.2 }}>
                Notification Center
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.88 }}>
                {loadingCount ? "Refreshing..." : `${unreadCount} unread announcement${unreadCount === 1 ? "" : "s"}`}
              </Typography>
            </Box>
            <Button
              size="small"
              disabled={markingAll || unreadItems < 1}
              onClick={handleMarkAllRead}
              sx={{
                color: "#fff",
                borderColor: alpha("#fff", 0.55),
                backgroundColor: alpha("#fff", 0.08),
                "&:hover": {
                  borderColor: "#fff",
                  backgroundColor: alpha("#fff", 0.14),
                },
              }}
              variant="outlined"
            >
              {markingAll ? "Saving..." : "Mark all read"}
            </Button>
          </Stack>
        </Box>

        {loadingList ? (
          <Stack
            sx={{ py: 5, alignItems: "center", justifyContent: "center", flex: 1 }}
            spacing={1.25}
          >
            <BrandSpinner compact label="Loading notifications…" />
          </Stack>
        ) : error ? (
          <Box sx={{ p: 2.5, flex: 1, overflowY: "auto" }}>
            <Typography sx={{ color: "error.main", fontWeight: 600 }}>
              {error}
            </Typography>
          </Box>
        ) : notifications.length < 1 ? (
          <Stack
            sx={{ p: 4, alignItems: "center", textAlign: "center", flex: 1, overflowY: "auto" }}
            spacing={1}
          >
            <CampaignRoundedIcon sx={{ color: alpha(BRAND_BLUE, 0.45), fontSize: 34 }} />
            <Typography sx={{ color: BRAND_BLUE, fontWeight: 700 }}>
              No announcements yet
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", maxWidth: 260 }}>
              Product updates, maintenance notices, and onboarding tips will appear here.
            </Typography>
          </Stack>
        ) : (
          <List
            disablePadding
            sx={{
              py: 0,
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
            }}
          >
            {notifications.map((notification, index) => {
              const config = severityConfig[notification.severity] || severityConfig.info;
              const SeverityIcon = config.icon;
              const isBusy = markingIds.includes(notification.id);
              const hasAction = notificationHasAction(notification);

              return (
                <React.Fragment key={notification.id}>
                  <ListItemButton
                    id={`notification-item-${notification.id}`}
                    onClick={() => handleNotificationAction(notification, true)}
                    sx={{
                      alignItems: "flex-start",
                      px: 2,
                      py: 1.75,
                      backgroundColor: notification.is_read
                        ? "#fff"
                        : alpha(BRAND_GREEN, 0.07),
                      borderLeft: `4px solid ${notification.is_read ? "transparent" : BRAND_GREEN}`,
                      boxShadow:
                        highlightedId === notification.id
                          ? `inset 0 0 0 2px ${alpha(BRAND_BLUE, 0.22)}`
                          : "none",
                      transition: "background-color .18s ease, transform .18s ease",
                      "&:hover": {
                        backgroundColor: alpha(config.color, 0.08),
                      },
                    }}
                  >
                    <Box
                      sx={{
                        mt: 0.25,
                        mr: 1.5,
                        width: 38,
                        height: 38,
                        borderRadius: "50%",
                        display: "grid",
                        placeItems: "center",
                        color: config.color,
                        backgroundColor: config.bg,
                        flexShrink: 0,
                      }}
                    >
                      <SeverityIcon fontSize="small" />
                    </Box>

                    <ListItemText
                      primary={
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ pr: 1 }}>
                          <Typography
                            sx={{
                              color: BRAND_BLUE,
                              fontWeight: notification.is_read ? 700 : 800,
                              lineHeight: 1.25,
                            }}
                          >
                            {notification.title}
                          </Typography>
                          {notification.is_banner && (
                            <Chip
                              label="Banner"
                              size="small"
                              sx={{
                                height: 22,
                                color: BRAND_BLUE,
                                fontWeight: 700,
                                backgroundColor: alpha(BRAND_BLUE, 0.1),
                              }}
                            />
                          )}
                          {!notification.is_read && (
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                backgroundColor: BRAND_GREEN,
                                boxShadow: `0 0 0 3px ${alpha(BRAND_GREEN, 0.18)}`,
                              }}
                            />
                          )}
                        </Stack>
                      }
                      secondary={
                        <Stack spacing={1} sx={{ mt: 0.7 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "text.primary",
                              whiteSpace: "pre-line",
                              wordBreak: "break-word",
                              maxHeight: 180,
                              overflowY: "auto",
                              pr: 0.5,
                            }}
                          >
                            {notification.message}
                          </Typography>

                          <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            spacing={1}
                            flexWrap="wrap"
                          >
                            <Typography variant="caption" sx={{ color: "text.secondary" }}>
                              {formatDateTime(
                                notification.created_at,
                                NOTIFICATION_TIMESTAMP_OPTIONS,
                              )}
                            </Typography>

                            {hasAction && (
                              <Button
                                size="small"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handleNotificationAction(notification, true);
                                }}
                                endIcon={<OpenInNewRoundedIcon sx={{ fontSize: 16 }} />}
                                sx={{
                                  color: config.color,
                                  fontWeight: 700,
                                  minWidth: 0,
                                  px: 0.25,
                                  "&:hover": {
                                    backgroundColor: "transparent",
                                    color: BRAND_BLUE,
                                  },
                                }}
                              >
                                Open
                              </Button>
                            )}
                          </Stack>
                        </Stack>
                      }
                    />

                    {isBusy && (
                      <CircularProgress
                        size={16}
                        sx={{ mt: 0.6, ml: 0.5, color: config.color, flexShrink: 0 }}
                      />
                    )}
                  </ListItemButton>
                  {notification.video_url && (
                    <Box sx={{
                      pl: 8.5, pr: 2, pb: 1.5,
                      backgroundColor: notification.is_read ? "#fff" : alpha(BRAND_GREEN, 0.07),
                      borderLeft: `4px solid ${notification.is_read ? "transparent" : BRAND_GREEN}`,
                    }}>
                      <NotificationVideo notification={notification} />
                    </Box>
                  )}
                  {index < notifications.length - 1 && <Divider />}
                </React.Fragment>
              );
            })}
          </List>
        )}
      </Popover>
    </>
  );
}
